import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SolutionsAppService {
  private myVariableSource = new BehaviorSubject<string[]>([]);

  public myVariable$ = this.myVariableSource.asObservable();

  constructor(private httpRequest: HttpClient) {
  }

  createSolution(solution: {}) {
    const url = '/solution_app/solution';
    return this.httpRequest.post(url, solution);
  }

  updateSolution(solution: any) {
    const url = `/solution_app/solution/${solution['solution_id']}/`;
    return this.httpRequest.post(url, solution);
  }

  deleteSolution(solution: any) {
    const url = `/solution_app/solution/${solution['solution_id']}/`;
    return this.httpRequest.delete(url);
  }

  getAvailableSolution(solution: any = null) {
    if (!solution) {
      const url = '/solution_app/solution';
      return this.httpRequest.get(url);
    } else {
      const url = `/solution_app/solution/${solution['solution_id']}/`;
      return this.httpRequest.get(url);
    }
  }

  createVersion(version: {}, solution: any) {
    const url = `/solution_app/${solution['solution_id']}/version/`;
    return this.httpRequest.post(url, version);
  }

  createVersionFromExistingVersion(
    solution: any,
    version: {},
    existingVersionId: string
  ) {
    const url = `/solution_app/${solution['solution_id']}/version/?copy_from=${existingVersionId}`;
    return this.httpRequest.post(url, version);
  }

  updateVersion(version: any, solution: any) {
    const url = `/solution_app/${solution['solution_id']}/version/${version['version_id']}/`;
    return this.httpRequest.put(url, version);
  }

  updateExecutionOrderOfProjectInVersion(versionId: string, order: Object) {
    const url = `/solution_app/version_run_order?conditions={"version_id":"${versionId}"}`;
    return this.httpRequest.put(url, order);
  }

  deleteVersion(version: any, solution: any) {
    const url = `/solution_app/${solution['solution_id']}/version/${version['version_id']}/`;
    return this.httpRequest.delete(url);
  }

  getAvailableVersion(solution: any, version: any = null) {
    if (!version) {
      const url = `/solution_app/${solution['solution_id']}/version/`;
      return this.httpRequest.get(url);
    } else {
      const url = `/solution_app/${solution['solution_id']}/version/${version['version_id']}/`;
      return this.httpRequest.get(url);
    }
  }

  getProjectsInVersion(solution: any, version: any) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/project/`;
    return this.httpRequest.get(url);
  }

  getDatasetsUsedInVersion(versionId: string) {
    const url = `/solution_app/form_config?version_id=${versionId}`;
    return this.httpRequest.get(url);
  }

  getNodesUsedInVersion(versionId: string) {
    const url = `/solution_app/form_config?version_id=${versionId}&list_nodes=true`;
    return this.httpRequest.get(url);
  }

  // Configurable form of version

  saveFrom(solution: any, version: any, formData: any) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/form/`;
    return this.httpRequest.post(url, formData);
  }

  getForm(solution: any, version: any) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/form/`;
    return this.httpRequest.get(url);
  }

  updateForm(solution: any, version: any, formId: string, data: any) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/form/${formId}/`;
    return this.httpRequest.put(url, data);
  }

  deleteForm(solution: any, version: any, formId: string, data: any) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/form/${formId}/`;
    return this.httpRequest.delete(url);
  }

  getFormResponse(solution: any, version: any, formId: string) {
    const url = `/solution_app/${solution?.solution_id}/${version?.version_id}/${formId}/response/`;
    return this.httpRequest.get(url);
  }

  saveFormResponse(solution: any, version: any, formId: string, data: any) {
    const url = `/solution_app/${solution?.solution_id}/${version?.version_id}/${formId}/response/`;
    return this.httpRequest.post(url, data);
  }

  updateFormResponse(
    solution: any,
    version: any,
    formId: string,
    responseId: string,
    data: any
  ) {
    const url = `/solution_app/${solution?.solution_id}/${version?.version_id}/${formId}/response/${responseId}/`;
    return this.httpRequest.put(url, data);
  }

  setMyVariable(value: string[]) {
    this.myVariableSource.next(value);
  }

  createProject(solution: any, version: any, project: {}) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/project/`;
    return this.httpRequest.post(url, project);
  }

  updateProject(solution: any, version: any, project: {}, projectId: string) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/project/${projectId}/`;
    return this.httpRequest.put(url, project);
  }

  getAvailableProjects(solution: any, version: any, projectId: string): any {
    let url = `/solution_app/${solution['solution_id']}/${version['version_id']}/project/`;
    if (projectId) {
      url += `${projectId}/`;
    }
    return this.httpRequest.get(url);
  }

  copyProjects(solution: any, version: any, info: any) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/copy_project/`;
    return this.httpRequest.post(url, info);
  }

  deleteProject(solution: any, version: any, projectId: string) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/project/${projectId}/`;
    return this.httpRequest.delete(url);
  }

  getProjectMarkedForSchedule(solution: any, version: any) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/scheduler/`;
    return this.httpRequest.get(url);
  }

  getProjectScheduledInVersion(solution: any, version: any) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/scheduler/`;
    return this.httpRequest.get(url);
  }

  getProjectScheduler(solution: any, version: any, project: any) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/${project['project_id']}/scheduler/`;
    return this.httpRequest.get(url);
  }

  saveProjectScheduler(solution: any, version: any, project: any, data: any) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/${project['project_id']}/scheduler/`;
    return this.httpRequest.post(url, data);
  }

  updateProjectScheduler(
    solution: any,
    version: any,
    projectScheduler: any,
    data: any
  ) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/scheduler/${projectScheduler['scheduler_id']}/`;
    return this.httpRequest.put(url, data);
  }

  deleteProjectScheduler(
    solution: any,
    version: any,
    projectScheduler: any
  ) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/scheduler/${projectScheduler['scheduler_id']}/`;
    return this.httpRequest.delete(url);
  }

  createPipeline(
    solution: any,
    version: any,
    projectId: any,
    pipelineObject: any
  ) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/${projectId}/pipeline/`;
    return this.httpRequest.post(url, pipelineObject);
  }

  update_pipeline(
    solution: any,
    version: any,
    projectId: any,
    pipelineId: string,
    pipelineObject: any
  ) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/${projectId}/pipeline/${pipelineId}/`;
    return this.httpRequest.put(url, pipelineObject);
  }

  deletePipeline(
    solution: any,
    version: any,
    projectId: any,
    pipelineId: string
  ) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/${projectId}/pipeline/${pipelineId}/`;
    return this.httpRequest.delete(url);
  }

  getPipeline(solution: any, version: any, projectId: any, pipelineId: string) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/${projectId}/pipeline/${pipelineId}/`;
    return this.httpRequest.get(url);
  }

  getConfiguredPipelines(solution: any, version: any, projectId: any) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/${projectId}/pipeline/`;
    return this.httpRequest.get(url);
  }

  getConfiguredTagsInProject(solution: any, version: any, projectId: any) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/${projectId}/tags/`;
    return this.httpRequest.get(url);
  }

  getavailablePipelines(): any {
    const url = '/solution_app/list_pipeline';
    return this.httpRequest.get(url);
  }

  getavailableNodes(pipeline_name: any) {
    const url = `/solution_app/list_pipeline/${pipeline_name}`;
    return this.httpRequest.get(url);
  }

  configureNode(
    solution: any,
    version: any,
    projectId: any,
    pipelineId: any,
    data: any
  ) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/${projectId}/${pipelineId}/node/`;
    return this.httpRequest.post(url, data);
  }

  updateNode(
    solution: any,
    version: any,
    projectId: any,
    pipelineId: any,
    nodeId: string,
    data: any
  ) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/${projectId}/${pipelineId}/node/${nodeId}/`;
    return this.httpRequest.put(url, data);
  }

  deleteNode(
    solution: any,
    version: any,
    projectId: any,
    pipelineId: any,
    nodeId: string
  ) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/${projectId}/${pipelineId}/node/${nodeId}/`;
    return this.httpRequest.delete(url);
  }

  getConfiguredNodesInPipeline(
    solution: any,
    version: any,
    projectId: any,
    pipelineId: any
  ) {
    const url = `/solution_app/${solution['solution_id']}/${version['version_id']}/${projectId}/${pipelineId}/node/`;
    return this.httpRequest.get(url);
  }

  /** Every upload/delete trigger of a version, across datasets. */
  getVersionTriggers(solutionId: string, versionId: string) {
    const url = `/solution_app/${solutionId}/${versionId}/trigger/`;
    return this.httpRequest.get(url);
  }

  runVersionOrProjectOrPipelineOrNode(
    solutionId: string,
    versionId: string,
    data: any
  ) {
    const url = `/solution_app/${solutionId}/${versionId}/run/`;
    return this.httpRequest.post(url, data);
  }

  getScheduledProject(solution: any, version: any, scheduler: any = null) {
    const schedulerSegment = scheduler ? `/${scheduler.project_id}` : '';
    const url = `/solution_app/${solution?.solution_id}/${version?.version_id}${schedulerSegment}/periodic_task/`;
    return this.httpRequest.get(url);
  }

  postScheduledProject(solution: any, version: any, scheduler: any, data: any) {
    const url = `/solution_app/${solution?.solution_id}/${version?.version_id}/${scheduler?.scheduler_id}/periodic_task/`;
    return this.httpRequest.post(url, data);
  }

  putScheduledProject(
    solution: any,
    version: any,
    scheduler: any,
    scheduled_id: string,
    data: any
  ) {
    const url = `/solution_app/${solution?.solution_id}/${version?.version_id}/${scheduler?.scheduler_id}/periodic_task/${scheduled_id}/`;
    return this.httpRequest.put(url, data);
  }

  deleteScheduledProject(
    solution: any,
    version: any,
    scheduler: any,
    scheduled_id: string
  ) {
    const url = `/solution_app/${solution?.solution_id}/${version?.version_id}/${scheduler?.scheduler_id}/periodic_task/${scheduled_id}/`;
    return this.httpRequest.delete(url);
  }

  saveDataValidationConfig(data: any) {
    const url = 'solution_app/data_validation';
    return this.httpRequest.post(url, data);
  }

  getDataValidationConfig(
    solution_id: string,
    version_id: string,
    data_name: string = ''
  ) {
    const conditions: any = {
      solution_id: solution_id,
      version_id: version_id,
    };
    if (data_name) {
      conditions['dataset_name'] = data_name;
    }
    const conditionsString = encodeURIComponent(JSON.stringify(conditions));
    const url = `solution_app/data_validation?conditions=${conditionsString}`;
    return this.httpRequest.get(url);
  }

  putDataValidationConfig(
    solution_id: string,
    version_id: string,
    data_name: string,
    data: any
  ) {
    const conditions: any = {
      solution_id: solution_id,
      version_id: version_id,
      dataset_name: data_name,
    };
    const conditionsString = encodeURIComponent(JSON.stringify(conditions));
    const url = `solution_app/data_validation?conditions=${conditionsString}`;
    return this.httpRequest.put(url, data);
  }

  deleteDataValidationConfig(
    solution_id: string,
    version_id: string,
    data_name: string
  ) {
    const conditions: any = {
      solution_id: solution_id,
      version_id: version_id,
      dataset_name: data_name,
    };
    const conditionsString = encodeURIComponent(JSON.stringify(conditions));
    const url = `solution_app/data_validation?conditions=${conditionsString}`;
    return this.httpRequest.get(url);
  }

  getTriggersByVersion(solution_id: string | any, version_id: string, data_name: string) {
    const url = `solution_app/${solution_id}/${version_id}/${data_name}/trigger/`
    return this.httpRequest.get(url)
  }

  getTriggers(solution_id: string, version_id: string, data_name: string) {
    const url = `solution_app/${solution_id}/${version_id}/${data_name}/trigger/`
    return this.httpRequest.get(url)
  }

  getTriggerById(solution_id: string, version_id: string, data_name: string, event_id: string) {
    const url = `solution_app/${solution_id}/${version_id}/${data_name}/trigger/${event_id}`
    return this.httpRequest.get(url)
  }

  postTriggers(solution_id: string | any, version_id: string, data_name: string, data: any) {
    const url = `solution_app/${solution_id}/${version_id}/${data_name}/trigger/`
    return this.httpRequest.post(url, data)
  }

  updateTriggers(solution_id: string | any, version_id: string, data_name: string, event_id: string, data: any) {
    const url = `solution_app/${solution_id}/${version_id}/${data_name}/trigger/${event_id}/`
    return this.httpRequest.put(url, data)
  }


  deleteTriggers(solution_id: string | any, version_id: string, data_name: string, event_id: string) {
    const url = `solution_app/${solution_id}/${version_id}/${data_name}/trigger/${event_id}/`
    return this.httpRequest.delete(url)
  }
}
